/**
 * The classify screen's yes/no gates — Step 1.
 *
 * Split out of `ClassifyStep` under the 350-line rule in
 * `docs/FILE_SIZE_POLICY.md` (ATTEST-UX-2 Bundle 2). Which questions belong to
 * which basis is `classifyQuestions.ts`'s decision and is not re-litigated
 * here; this renders the set it is handed, in the order it is handed them.
 *
 * Renders nothing at all for an empty set rather than an empty bordered grid —
 * a section with a heading and no contents reads as one that failed to load
 * rather than one that has no questions (ATTEST-BASIS-UNIFY-1).
 */
import type {
  ClassifyBooleanField,
  ClassifyQuestion,
} from '@/components/attest/classifyQuestions'
import { Label } from '@/components/ui/label'

function YesNo({
  id, label, help, value, onChange,
}: {
  id: string
  label: string
  help: string
  value: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="rounded border border-border p-3">
      <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
      <p className="mt-1 text-xs text-muted-foreground">{help}</p>
      <select
        id={id}
        data-testid={id}
        className="mt-2 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
        value={value ? 'yes' : 'no'}
        onChange={e => onChange(e.target.value === 'yes')}
      >
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  )
}

export interface ClassifyQuestionGridProps {
  questions: readonly ClassifyQuestion[]
  /** The engagement type these questions were selected for — read by tests. */
  engagementType: string
  answerOf: (field: ClassifyBooleanField) => boolean
  onAnswer: (field: ClassifyBooleanField, value: boolean) => void
}

export function ClassifyQuestionGrid({
  questions, engagementType, answerOf, onAnswer,
}: ClassifyQuestionGridProps) {
  if (questions.length === 0) return null
  return (
    <div
      className="grid gap-3 md:grid-cols-2"
      data-testid="classify-questions"
      data-engagement-type={engagementType}
    >
      {questions.map(q => (
        <YesNo
          key={q.id}
          id={q.id}
          label={q.label}
          help={q.help}
          value={answerOf(q.field)}
          onChange={v => onAnswer(q.field, v)}
        />
      ))}
    </div>
  )
}
